import React, {FC, ReactElement, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {globalDocument} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  computeInvertedEditorColor,
  findHslaColor,
} from '../../../../../libs/shared/ui/src/styles/darkModeEditorHelpers';
import {growToFitHeightStyle} from '../../../../../libs/shared/ui/src/styles/styleHelpers';
import {isExpandedDarkTheme} from '../../../../../libs/shared/ui/src/theme/themeTypeHelpers';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {emailStyle} from '../composerStyles';

/*
 * Props.
 */

export interface EmailComposerEditorBaseProps {
  className?: string;
  children: RendererOf<EmailComposerEditorProps>;
  /** Adjust spacing for the first editor. */
  isFirstEditor?: boolean;
  /** Apply this user default style CSS. */
  defaultStyle?: string;
  /** Apply a faux blockquote style to the content, such as for a reply quote. */
  shouldAddQuoteStyle?: boolean;
}

export interface EmailComposerEditorProps {
  className?: string;
}

/*
 * Style.
 */

const firstEditorStyle = css`
  padding-top: 16px;
`;

const editorStyle = css<EmailComposerEditorBaseProps>`
  padding: 8px 16px;
  min-height: 16px;

  ${(p) => (p.isFirstEditor ? firstEditorStyle : '')};
  ${growToFitHeightStyle};

  ${emailStyle};
  background-color: transparent;
  ${(p) => maybeAddDarkModeColorMask(p.theme, p.defaultStyle)};
`;

const StyledQuoteDiv = styled.div`
  position: relative;
  padding-left: 11px;

  &::before {
    content: ' ';
    position: absolute;
    left: 16px;
    top: 8px;
    bottom: 8px;
    border-left: 1px solid ${(p) => p.theme.palette.grey.shade30};
  }
`;

/*
 * Component.
 */

// Forwards the className for the editor styling to the render prop.
const EmailComposerEditorBaseComponent: FC<EmailComposerEditorBaseProps> = ({
  className,
  shouldAddQuoteStyle,
  children,
}) => maybeWrapInQuote(children({className}), !!shouldAddQuoteStyle);

export const EmailComposerEditorBase = styled(EmailComposerEditorBaseComponent)`
  ${editorStyle};
`;

/*
 * Helpers.
 */

function maybeWrapInQuote(children: ReactNode, shouldWrap: boolean): ReactElement {
  if (!shouldWrap) {
    return <>{children}</>;
  }

  return <StyledQuoteDiv>{children}</StyledQuoteDiv>;
}

// The defaultStyle is saved when the user selects "Save as default" in the composer.
// In dark mode, the color style needs to be inverted.
function maybeAddDarkModeColorMask(theme: UserTheme, defaultStyle?: string) {
  if (!isExpandedDarkTheme(theme) || !defaultStyle) {
    return defaultStyle;
  }

  const element = globalDocument.createElement('span');
  const {style} = element;
  style.cssText = defaultStyle;

  const hslaColor = findHslaColor(style.color);
  if (!hslaColor) {
    return defaultStyle;
  }

  const invertedColor = computeInvertedEditorColor(hslaColor);
  return css`
    ${defaultStyle};
    color: ${invertedColor};
  `;
}
